import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Avatar from '../components/ui/Avatar';
import Badge from '../components/ui/Badge';
import ListingCard from '../components/ListingCard';

export default function PublicProfile() {
  const navigate = useNavigate();
  const { userId } = useParams();

  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAllCategories, setShowAllCategories] = useState(false);

  const ITEMS_PER_PAGE = 6;
  const MAX_VISIBLE_CATEGORIES = 5;

  useEffect(() => {
    if (!userId) return navigate('/');
    Promise.all([
      axios.get(`${process.env.REACT_APP_API_URL}/api/users/${userId}/profile`),
      axios.get(`${process.env.REACT_APP_API_URL}/api/users/${userId}/listings`),
    ])
      .then(([profileRes, listingsRes]) => {
        setProfile(profileRes.data.profile);
        setListings(listingsRes.data.listings);
      })
      .catch(() => { navigate('/'); })
      .finally(() => setLoading(false));
  }, [userId, navigate]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter]);

  if (loading)
    return (
      <div className="bg-sand-50 min-h-screen flex items-center justify-center">
        <div
          role="status"
          aria-label="Loading"
          className="w-8 h-8 border-2 border-sand-200 border-t-terra-400 rounded-full animate-spin"
        />
      </div>
    );

  if (!profile) return null;

  const allCategories = ['All', ...new Set(listings.map(l => l.category).filter(Boolean))];

  // Category display logic
  const displayedCategories = showAllCategories
    ? allCategories
    : allCategories.slice(0, MAX_VISIBLE_CATEGORIES);
  const hasMoreCategories = allCategories.length > MAX_VISIBLE_CATEGORIES;

  // Filtering and pagination
  const filtered = activeFilter === 'All' ? listings : listings.filter(l => l.category === activeFilter);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedListings = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const memberYear = profile.created_at
    ? new Date(profile.created_at).getFullYear()
    : null;

  return (
    <div className="bg-sand-50 min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

        {/* Back */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-ink-400 hover:text-ink-700 mb-6 transition-colors duration-200"
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
          Back
        </button>

        {/* Profile header */}
        <div className="bg-white border border-sand-200 rounded-lg p-6 shadow-card mb-8 flex flex-col sm:flex-row gap-6 items-start sm:items-center">
          <Avatar
            src={profile?.avatar_url}
            name={`${profile?.first_name} ${profile?.last_name}`}
            size="xl"
          />
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap mb-1">
              <p className="font-heading text-2xl font-semibold text-ink-900">
                {profile?.first_name} {profile?.last_name}
              </p>
              <Badge variant="category" className="capitalize">
                {profile?.role}
              </Badge>
            </div>
            {profile?.location && (
              <p className="text-sm text-ink-400 mb-1">{profile.location}</p>
            )}
            {profile?.bio && (
              <p className="text-sm text-ink-400 leading-relaxed max-w-lg">
                {profile.bio}
              </p>
            )}
          </div>

          {/* Stats row */}
          <div className="flex gap-6 shrink-0 flex-wrap">
            <div className="text-center">
              <p className="font-heading text-xl font-semibold text-terra-400">
                {listings?.length ?? 0}
              </p>
              <p className="text-xs text-ink-400">Listings</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-xl font-semibold text-terra-400">
                {profile.avg_rating ? profile.avg_rating.toFixed(1) : '—'}
              </p>
              <p className="text-xs text-ink-400">Rating</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-xl font-semibold text-terra-400">
                {profile.total_orders ? `${profile.total_orders}+` : '—'}
              </p>
              <p className="text-xs text-ink-400">Orders</p>
            </div>
            {memberYear && (
              <div className="text-center">
                <p className="font-heading text-xl font-semibold text-terra-400">
                  {memberYear}
                </p>
                <p className="text-xs text-ink-400">Member since</p>
              </div>
            )}
          </div>
        </div>

        {/* Category filters */}
        {allCategories.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
            {displayedCategories.map(cat => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveFilter(cat)}
                className={[
                  'px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 shrink-0',
                  activeFilter === cat
                    ? 'bg-terra-400 text-white shadow-card'
                    : 'bg-white border border-sand-200 text-ink-700 hover:border-terra-400/50',
                ].join(' ')}
              >
                {cat}
              </button>
            ))}
            {hasMoreCategories && (
              <button
                type="button"
                onClick={() => setShowAllCategories(!showAllCategories)}
                className="px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 shrink-0 bg-white border border-sand-200 text-terra-400 hover:border-terra-400/50"
              >
                {showAllCategories
                  ? 'Show less'
                  : `+${allCategories.length - MAX_VISIBLE_CATEGORIES} more`}
              </button>
            )}
          </div>
        )}

        {/* Listings grid */}
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center py-16 gap-3">
            <p className="font-heading text-lg font-semibold text-ink-900">
              No listings yet
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            {paginatedListings.map(listing => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-1">
            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 rounded-lg text-sm font-medium transition-all duration-200 bg-white border border-sand-200 text-ink-700 hover:border-terra-400/50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>

            {[...Array(totalPages)].map((_, idx) => {
              const page = idx + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <button
                    key={page}
                    type="button"
                    onClick={() => setCurrentPage(page)}
                    className={[
                      'w-9 h-9 rounded-lg text-sm font-medium transition-all duration-200',
                      page === currentPage
                        ? 'bg-terra-400 text-white shadow-card'
                        : 'bg-white border border-sand-200 text-ink-700 hover:border-terra-400/50',
                    ].join(' ')}
                  >
                    {page}
                  </button>
                );
              }
              if (page === currentPage - 2 || page === currentPage + 2) {
                return (
                  <span key={page} className="w-9 h-9 flex items-center justify-center text-ink-400 text-sm">
                    ...
                  </span>
                );
              }
              return null;
            })}

            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 rounded-lg text-sm font-medium transition-all duration-200 bg-white border border-sand-200 text-ink-700 hover:border-terra-400/50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
